<template>
    <card class="mb-0">
        <div slot="header">
            <slot name="header">
                <h3 v-if="title" class="card-title">{{ title }}</h3>
                <h5 v-if="subTitle" class="card-category mt-1">
                    {{ subTitle }}
                </h5>
            </slot>
        </div>
        <div v-if="!hidePerPage">
            <div :class="[$style.actions, 'mb-3']">
                <div class="d-flex align-center">
                    <el-select
                        v-model="pagination.perPage"
                        class="select-primary pagination-select"
                        placeholder="Per page"
                    >
                        <el-option
                            v-for="item in pagination.perPageOptions"
                            :key="item"
                            class="select-primary"
                            :label="item"
                            :value="item"
                        />
                    </el-select>
                    <slot name="filters" />
                </div>
            </div>
        </div>
        <div class="pl-0 col-lg-4 col-md-6 col-sm-12 pb-3">
            <base-input>
                <el-input
                    v-model="searchQuery"
                    type="search"
                    class="search-input"
                    prefix-icon="el-icon-search"
                    placeholder="Search records"
                    aria-controls="datatables"
                />
            </base-input>
        </div>
        <slot />
        <div slot="footer" class="col-12" :class="$style.footer">
            <p id="tableTotalText" class="card-category">
                Showing {{ from + (total === 0 ? 0 : 1) }} to {{ to }} of
                {{ total }} Entries
            </p>
            <base-pagination
                v-model="pagination.currentPage"
                class="pagination-no-border"
                :per-page="pagination.perPage"
                :total="total"
                @input="$emit('changedPage', $event)"
            />
        </div>
    </card>
</template>

<script>
import _ from "lodash";
import Fuse from "fuse.js";
import { BasePagination } from "src/components/UI";

export default {
    name: "TableSearchAndPagination",
    components: {
        BasePagination,
    },
    props: {
        title: {
            type: String,
            default: "",
        },
        subTitle: {
            type: String,
            default: "",
        },
        virtualTotal: {
            type: Number,
            default: 0,
        },
        tableData: {
            type: Array,
            required: true,
        },
        perPage: {
            type: Number,
            default: 25,
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        extraSearchProps: {
            type: Array,
            default: () => [],
        },
        query: {
            type: String,
            default: "",
        },
        customSearch: {
            type: Boolean,
            default: false,
        },
        hidePerPage: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            pagination: {
                perPage: this.perPage,
                currentPage: this.currentPage,
                perPageOptions: [5, 10, 25, 50],
            },
            searchQuery: this.query,
            searchedData: [],
            fuseSearch: null,
        };
    },
    computed: {
        dataSearchProps() {
            if (!this.tableData.length) return [];
            let props = [];
            // build search props based on first object
            Object.entries(this.tableData[0]).forEach(([key, value]) => {
                // if the value is an object, we will plunge 1 level further for our search
                if (_.isPlainObject(value)) {
                    Object.entries(value).forEach(([key2, value2]) => {
                        // create a string representation for the search key
                        if (!_.isObject(value2)) {
                            props.push(`${key}.${key2}`);
                        }
                    });
                } else {
                    props.push(key);
                }
            });
            props = props.filter((item) => !item.includes("__typename"));
            return [...props, ...this.extraSearchProps];
        },
        /** *
         * Returns a page from the searched data or the whole data.
         * Search is performed in the watch section below
         */
        queriedData() {
            if (this.customSearch) {
                return this.tableData.slice(this.from, this.to);
            }
            let result = this.tableData;
            if (this.searchQuery.trim() !== "") {
                result = this.searchedData;
            }
            return result.slice(this.from, this.to);
        },
        to() {
            let highBound = this.from + this.pagination.perPage;
            if (this.total < highBound) {
                highBound = this.total;
            }
            return highBound;
        },
        from() {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        total() {
            if (this.virtualTotal) return this.virtualTotal;
            return this.searchQuery.trim() !== ""
                ? this.searchedData.length
                : this.tableData.length;
        },
    },
    watch: {
        "pagination.perPage": function update(value) {
            this.$emit("perPageUpdated", value);
        },
        queriedData(data) {
            this.$emit("dataUpdated", data);
        },
        /**
         * Searches through the table data by a given query.
         * NOTE: If you have a lot of data, it's recommended to do the search on the
         * Server Side and only display the results here.
         * @param value of the query
         */
        searchQuery(value) {
            if (!this.customSearch) {
                let result = this.tableData;
                if (value !== "" && this.fuseSearch) {
                    result = this.fuseSearch.search(this.searchQuery);
                }
                this.searchedData = result;
            }
            this.$emit("queryUpdated", value);
        },
        tableData() {
            if (!this.customSearch) {
                this.fuseSearch = new Fuse(this.tableData, {
                    keys: this.dataSearchProps,
                    threshold: 0.3,
                });
            }
        },
    },
    created() {
        if (!this.customSearch) {
            this.fuseSearch = new Fuse(this.tableData, {
                keys: this.dataSearchProps,
                threshold: 0.1,
            });
        }
        this.$emit("dataUpdated", this.queriedData);
    },
};
</script>

<style>
.align-center {
    align-items: center;
}
</style>

<style module>
.actions,
.footer {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
}
</style>
